import { querySerialize } from '../query'
import { urlParse } from './urlParse'

const validPattern = /^(\/?(:\w+|[\w-~%]+))?(\/(:\w+|[\w-~%]+))*\/?$/i
const separatorPattern = /\/+/

const isParam = (part: string) => part[0] === ':'

export type RouteMap = Record<string, string>
export type Page = {
	name: string
	params?: Record<string, string | number> | null
	query?: Record<string, string | number | boolean | Array<string> | null | undefined>
	fragment?: string
}
export type PageStrict = {
	name: string
	params?: Record<string, string>
	query?: Record<string, string | Array<string> | boolean>
	fragment?: string
}
export type Router = {
	match: (path: string) => PageStrict | null
	getPath: (page: Page, origin?: string) => string | null
	extendPage: (page: Page) => Page
	names: Array<string>
}

export const routerCreate = (routeMap: RouteMap): Router => {
	const routes = Object.entries(routeMap)
		.map(([name, pattern]) => {
			if (typeof pattern !== 'string' || !validPattern.test(pattern)) {
				throw new Error(`Invalid router pattern '${pattern}'`)
			}
			const parts = pattern.split(separatorPattern)
			const matchParts = parts
				// Remove empty strings if leading or trailing slashes are present
				.filter(Boolean)
				// Lower case to allow case-insensitive match of static parts
				.map((part) =>
					isParam(part)
						? ({ type: 'param', value: part.substring(1) } as const)
						: ({ type: 'literal', value: decodeURIComponent(part).toLowerCase() } as const),
				)

			return { name, pattern, parts, matchParts }
		})
		.sort((a, b) => {
			// Compare match parts
			for (let i = 0; i < a.matchParts.length && i < b.matchParts.length; i++) {
				// If a is a param, b should be first
				if (a.matchParts[i].type === 'param' && b.matchParts[i].type === 'literal') {
					return 1
				}
				// If b is a param, a should be first
				if (a.matchParts[i].type === 'literal' && b.matchParts[i].type === 'param') {
					return -1
				}
				// If parts differ, sort alphabetically
				if (a.matchParts[i].value !== b.matchParts[i].value) {
					return a.matchParts[i].value < b.matchParts[i].value ? -1 : 1
				}
			}
			// If pattern match up to shortest, sort by length
			if (a.matchParts.length !== b.matchParts.length) {
				return a.matchParts.length - b.matchParts.length
			}
			// Patterns are exactly the same
			return 0
		})

	const names = routes.map(({ name }) => name)

	const match: Router['match'] = (url) => {
		const { path, query, fragment } = urlParse(url)
		const requestParts = path.map((part) => ({ value: part, match: part.toLowerCase() }))

		const route = routes.find(
			({ matchParts }) =>
				requestParts.length === matchParts.length &&
				matchParts.every((part, index) => part.type === 'param' || part.value === requestParts[index].match),
		)

		if (!route) return null

		const params = route.matchParts.reduce<Record<string, string>>((obj, part, index) => {
			if (part.type === 'param') {
				obj[part.value] = requestParts[index].value
			}
			return obj
		}, {})

		return {
			name: route.name,
			params,
			query,
			fragment,
		}
	}

	const getPath: Router['getPath'] = ({ name, params, query = {}, fragment = '' }, base) => {
		const route = routes.find(({ name: routeName }) => name === routeName)

		if (!route) {
			return null
		}

		const path = route.parts
			.map((part) => {
				if (!isParam(part)) {
					return part
				}
				const prop = part.substring(1)
				if (!params || !(prop in params)) {
					throw new Error(`Cannot build url for pattern '${route.pattern}' without param '${prop}'`)
				}
				return encodeURIComponent(String(params[prop]))
			})
			.join('/')

		const parts = []

		if (base) {
			parts.push(base.endsWith('/') ? base.slice(0, -1) : base)
			parts.push('/')
			parts.push(path.startsWith('/') ? path.slice(1) : path)
		} else {
			parts.push(path)
		}
		parts.push(querySerialize(query, '?'))

		const fragmentValue = fragment.startsWith('#') ? fragment.slice(1) : fragment

		if (fragmentValue) {
			parts.push('#')
			parts.push(encodeURIComponent(fragmentValue))
		}

		return parts.join('')
	}

	const extendPage: Router['extendPage'] = (page) => {
		const url = getPath(page)

		if (url === null) {
			throw new Error(`Unknown page name '${page.name}', routing setup for ${names.join(', ')}`)
		}

		return {
			...page,
			params: { ...page.params, url },
		}
	}

	return {
		match,
		getPath,
		extendPage,
		names,
	}
}
